import { getRelayString, getYesNoString } from "../../../helpers/Device";
import { getDevicePresence } from "../../../helpers/DevicePresence";
import { getDeviceOfflineState } from "../../../helpers/DeviceOfflineState";

const getFiniteNumber = (value) => {
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const getRelayStatus = (value) => {
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "number") {
		return value === 1 ? true : value === 0 ? false : null;
	}
	if (typeof value === "string") {
		const normalised = value.trim().toLowerCase();
		if (["1", "true", "closed", "close", "on"].includes(normalised)) {
			return true;
		}
		if (["0", "false", "open", "off"].includes(normalised)) {
			return false;
		}
	}
	return null;
};

const getAlarmTypeLabel = (type) => {
	switch (type) {
		case 1:
			return "Error";
		case 2:
			return "Leak";
		case 3:
			return "Flow Rate";
		case 4:
			return "No Flow";
		case 5:
			return "Flow Duration";
		case 6:
			return "Flow Volume";
		case 7:
			return "Temperature";
		case 8:
			return "Microleak";
		case 9:
			return "Valve Fail";
		default:
			return "Unknown";
	}
};

const getAlarmThresholdUnit = (type) => {
	switch (type) {
		case 2:
			return "kΩ";
		case 3:
			return "L/min";
		case 5:
			return "s";
		case 6:
			return "L";
		case 7:
			return "°C";
		default:
			return "";
	}
};

const getOutputModeLabel = (mode) => {
	switch (mode) {
		case 0:
			return "Disabled";
		case 1:
			return "Any Alarm";
		case 2:
			return "Power Loss";
		case 3:
			return "Mirror Valve";
		default:
			return "---";
	}
};

/**
 * @typedef {Object} DeviceDetailsViewModel
 * @property {Object} supports
 * @property {Object} presence
 * @property {Object[]} pendingUpdates
 * @property {Object} overview
 * @property {Object} liveState
 * @property {Object} config
 * @property {Object} diagnostics
 * @property {Object[]} alarmsTableRows
 */

/**
 * Build the render-ready view model for Device Details.
 * This is an internal UI contract and does not change backend payloads.
 */
export const buildDeviceDetailsViewModel = ({
	deviceId,
	deviceInfo,
	reportedState,
	modbusState,
	desiredState,
	desiredStateInfo,
	isAdmin,
	commsEvent,
}) => {
	if (!deviceInfo || !reportedState) {
		return null;
	}

	const wakePendingValue = getFiniteNumber(desiredStateInfo?.wake_interval?.value);
	const keepalivePendingValue = getFiniteNumber(desiredStateInfo?.keepalive_interval?.value);
	const wakeIntervalValue = getFiniteNumber(reportedState?.wake_interval);
	const keepaliveIntervalValue = getFiniteNumber(reportedState?.keepalive_interval);

	const pendingUpdates = [];
	const desiredRelayStatus = getRelayStatus(desiredState?.relay?.status ?? desiredStateInfo?.relay?.value);
	const reportedRelayStatus = getRelayStatus(reportedState?.relay?.status);
	if (
		desiredState?.relay !== undefined &&
		desiredRelayStatus !== null &&
		reportedRelayStatus !== null &&
		desiredRelayStatus !== reportedRelayStatus
	) {
		pendingUpdates.push({
			key: "relay",
			labelKey: "device.state.relay",
			value: getRelayString(desiredRelayStatus),
			createdBy: desiredStateInfo?.relay?.createdBy || "Unknown",
			createdAt: desiredStateInfo?.relay?.createdAt,
		});
	}
	if (desiredState?.wake_interval !== undefined && wakePendingValue !== null) {
		pendingUpdates.push({
			key: "wake_interval",
			labelKey: "device.config.interval.wakeInterval",
			value: `${wakePendingValue} seconds`,
			createdBy: desiredStateInfo?.wake_interval?.createdBy || "Unknown",
			createdAt: desiredStateInfo?.wake_interval?.createdAt,
		});
	}
	if (desiredState?.keepalive_interval !== undefined && keepalivePendingValue !== null) {
		pendingUpdates.push({
			key: "keepalive_interval",
			labelKey: "device.config.interval.keepInterval",
			value: `${keepalivePendingValue} seconds`,
			createdBy: desiredStateInfo?.keepalive_interval?.createdBy || "Unknown",
			createdAt: desiredStateInfo?.keepalive_interval?.createdAt,
		});
	}

	const fs2Config = reportedState?.config;
	const fs2Alarms = Array.isArray(fs2Config?.alarms) ? fs2Config.alarms : [];
	const presence = getDevicePresence({
		deviceId,
		reportedState,
		fallbackUpdatedAt: deviceInfo?.updatedAt,
	});
	//Offline follows the backend's connection-lost event. presence stays for the device family,
	//the last seen timestamp and the estimate the warning compares against.
	const offlineState = getDeviceOfflineState({ presence, commsEvent });
	const alarmsTableRows = fs2Alarms.map((alarm, idx) => {
		const alarmLabel = `${getAlarmTypeLabel(alarm?.type)}${
			alarm?.sensor !== undefined ? ` (S${Number(alarm.sensor) + 1})` : ""
		}`;
		const thresholdUnit = getAlarmThresholdUnit(alarm?.type);
		const thresholdValue = alarm?.threshold;
		const threshold =
			thresholdValue !== undefined && thresholdValue !== null
				? `${thresholdValue}${thresholdUnit ? ` ${thresholdUnit}` : ""}`
				: "---";

		return {
			id: `${alarm?.type || "unknown"}-${alarm?.sensor || "na"}-${idx}`,
			alarm: alarmLabel,
			enabled: getYesNoString(alarm?.enabled),
			threshold,
			audible: getYesNoString(alarm?.audible),
		};
	});

	return {
		presence,
		offlineState,
		supports: {
			hasInput: Boolean(deviceInfo?.inputDevice?.length),
			hasOutput: Boolean(deviceInfo?.outputDevice?.length),
			isRanger: deviceInfo?.inputDevice?.includes("RANGER"),
			// Same family test as presence, so a device cannot be an FS2 here and a
			// CloudConnect there. This used to sniff the reported state directly and
			// counted `temperature` as an FS2 marker, which misread every
			// CloudConnect on firmware 1.2.x once it began reporting chip die
			// temperature.
			isFs2: presence.family === "FS2",
			isAdmin,
		},
		pendingUpdates,
		overview: {
			deviceId,
			deviceInfo,
		},
		liveState: {
			reportedState,
			modbusState,
			deviceInfo,
		},
		config: {
			reportedState,
			wakeIntervalValue,
			keepaliveIntervalValue,
			fs2Config,
			alarmsTableRows,
			outputModeLabels: {
				relay1: getOutputModeLabel(fs2Config?.outputs?.relay1?.mode),
				relay2: getOutputModeLabel(fs2Config?.outputs?.relay2?.mode),
			},
		},
		diagnostics: {
			reportedState,
			deviceInfo,
		},
		alarmsTableRows,
	};
};
